// Setup › Job types on the SettingsPage pattern (BL-189): the kinds of job the company books
// (jobTypes.list), five and then Show all, each with its department, length, skills and the licences it
// needs; then the licences the company tracks (licenceKinds.list), which a job type can need (BL-027).
// As Setup.tsx's OrgTab draws them in Blueprint (show 'types'), with nothing changed: Add a job type, Add
// a licence and each row's Edit open a FormSheet; the owner or a manager changes them (org.edit). While
// the company mirrors its import source a job type's name, department and offering are the source's, its
// defaults stay the company's (BL-098), with the line. The departments are Company details'. Riverstone
// keeps OrgTab.
import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Choice, ChoiceGroup, Link, SelectField, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { BP_LEDE } from './ledes.ts';
import { kindForm, kindInput, type LicenceKind } from './licences-model.ts';
import { settingsPage } from './parts.ts';
import {
  jobTypeDefaultsInput,
  jobTypeForm,
  jobTypeInput,
  setupRights,
  type JobType,
  type Unit,
} from './setup-model.ts';

const FIVE = 5;
const LICENCES_LINE =
  'The licences and certifications your people hold. A job type can need one; each person’s are on their profile, reminded before they expire.';
const NEEDS_LINE =
  'Someone on each visit holds every one ticked, valid on the visit’s day; your settings say whether a visit without warns or is refused.';

/** One list from the server, loaded once and again on reload(). */
function useRows<T>(action: string, pick: (r: never) => T[]) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    client
      .call<never>(action, { includeInactive: true })
      .then((r) => {
        setRows(pick(r));
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
    // pick is a literal per call site
  }, [action]);
  useEffect(load, [load]);
  return { rows, error, load };
}
const statusOf = (rows: readonly unknown[] | null, error: string | null) =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

export function JobTypesPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const canEdit = setupRights(held).org;
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  const units = useRows<Unit>('businessUnits.list', (r: { businessUnits: Unit[] }) => r.businessUnits);
  const kinds = useRows<LicenceKind>('licenceKinds.list', (r: { kinds: LicenceKind[] }) => r.kinds);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('org').label ?? 'Job types',
            lede: BP_LEDE.org,
          }}
        >
          <JobTypes
            canEdit={canEdit}
            mirroring={mirroring}
            units={units.rows ?? []}
            kinds={kinds.rows ?? []}
          />
          <Licences canEdit={canEdit} list={kinds} />
        </SettingsPage>
      </main>
    </div>
  );
}

function JobTypes({
  canEdit,
  mirroring,
  units,
  kinds,
}: {
  canEdit: boolean;
  mirroring: boolean;
  units: Unit[];
  kinds: LicenceKind[];
}) {
  const types = useRows<JobType>('jobTypes.list', (r: { jobTypes: JobType[] }) => r.jobTypes);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<JobType | 'new' | null>(null);
  const { rows } = types;
  const shown = rows && !all ? rows.slice(0, FIVE) : rows;
  const more = rows && !all && rows.length > FIVE;
  const unitName = (id: string | null) => units.find((u) => u.id === id)?.name ?? 'Any department';
  const kindName = (id: string) => kinds.find((k) => k.id === id)?.name ?? 'a licence';
  return (
    <>
      <SettingsSection
        title="Your job types"
        {...(canEdit && !mirroring
          ? {
              action: (
                <Button variant="primary" onPress={() => setOpen('new')}>
                  Add a job type
                </Button>
              ),
            }
          : {})}
        {...(mirroring && canEdit
          ? {
              note: `Departments’ and job types’ names: ${MIRROR_LINE} Their defaults, a department’s customer text and the licences stay yours.`,
            }
          : {})}
        status={statusOf(rows, types.error)}
        emptyText="No job types yet."
        {...(types.error ? { failedText: types.error } : {})}
        onRetry={types.load}
        {...(more ? { foot: <Link onPress={() => setAll(true)}>Show all {rows.length}</Link> } : {})}
      >
        {shown?.map((j) => (
          <SettingRow
            key={j.id}
            name={j.name}
            {...(j.active ? {} : { tag: 'Not offered' })}
            help={[
              unitName(j.businessUnitId),
              `${j.defaultDurationMinutes} min`,
              ...(j.skills.length ? [j.skills.join(', ')] : []),
              ...(j.requiredLicenceKindIds.length
                ? [`needs ${j.requiredLicenceKindIds.map(kindName).join(', ')}`]
                : []),
            ].join(' · ')}
            control={
              canEdit ? (
                <Link label={`Edit ${j.name}`} onPress={() => setOpen(j)}>
                  Edit
                </Link>
              ) : null
            }
          />
        ))}
      </SettingsSection>
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {open && (
        <JobTypeSheet
          key={open === 'new' ? 'new' : open.id}
          type={open === 'new' ? null : open}
          ownOnly={mirroring}
          units={units}
          kinds={kinds}
          onSaved={types.load}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function JobTypeSheet({
  type,
  ownOnly,
  units,
  kinds,
  onSaved,
  onClose,
}: {
  type: JobType | null;
  /** While the company mirrors its import source: its own columns only, never its name, department or offering (BL-098). */
  ownOnly: boolean;
  units: Unit[];
  /** The licences the company tracks (BL-027), retired ones too, for a type that still names one. */
  kinds: LicenceKind[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [first] = useState(() => jobTypeForm(type));
  const [f, setF] = useState(first);
  const save = useSaveKey(JSON.stringify(f));
  const offered = kinds.filter((k) => k.active || f.licences.includes(k.id));
  return (
    <FormSheet
      isOpen
      title={type ? type.name : 'Add a job type'}
      {...(ownOnly ? { line: `Its name, department and offering: ${MIRROR_LINE}` } : {})}
      submitLabel={type ? 'Save' : 'Add job type'}
      isDirty={JSON.stringify(f) !== JSON.stringify(first)}
      isSubmitDisabled={!f.name.trim()}
      onClose={onClose}
      onSubmit={async () => {
        const built = ownOnly && type ? jobTypeDefaultsInput(type, f) : jobTypeInput(type, f);
        if ('error' in built) throw new Error(built.error);
        const action =
          ownOnly && type ? 'jobTypes.setDefaults' : type ? 'jobTypes.update' : 'jobTypes.create';
        try {
          await client.call(action, built.input, { idempotencyKey: save.key });
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved();
      }}
    >
      <TextField
        label="Name"
        value={f.name}
        isDisabled={ownOnly}
        autoFocus
        onChange={(name) => setF({ ...f, name })}
      />
      <SelectField
        label="Department"
        value={f.businessUnitId}
        isDisabled={ownOnly}
        options={[
          { value: '', label: 'Any department' },
          ...units
            .filter((u) => u.active || u.id === f.businessUnitId)
            .map((u) => ({ value: u.id, label: u.name })),
        ]}
        onChange={(businessUnitId) => setF({ ...f, businessUnitId })}
      />
      <SelectField
        label="Usually"
        value={f.defaultKind}
        options={[
          { value: '', label: 'Either' },
          { value: 'service', label: 'Service (the work)' },
          { value: 'estimate_visit', label: 'An estimate visit' },
        ]}
        onChange={(v) => setF({ ...f, defaultKind: v as typeof f.defaultKind })}
      />
      <TextField
        label="Usual length (minutes)"
        inputMode="numeric"
        width="number"
        value={f.duration}
        onChange={(duration) => setF({ ...f, duration })}
      />
      <TextField
        label="Skills it needs"
        value={f.skills}
        placeholder="gas, backflow"
        onChange={(skills) => setF({ ...f, skills })}
      />
      {offered.length > 0 && (
        <ChoiceGroup
          label="Licences it needs"
          description={NEEDS_LINE}
          value={f.licences}
          onChange={(licences) => setF({ ...f, licences })}
        >
          {offered.map((k) => (
            <Choice key={k.id} value={k.id}>
              {k.active ? k.name : `${k.name} (no longer tracked)`}
            </Choice>
          ))}
        </ChoiceGroup>
      )}
      {type && !ownOnly && (
        <Checkbox isSelected={f.active} onChange={(active) => setF({ ...f, active })}>
          Offered when booking
        </Checkbox>
      )}
    </FormSheet>
  );
}

function Licences({
  canEdit,
  list,
}: {
  canEdit: boolean;
  /** The page's own list, so its job types see a licence added here at once. */
  list: ReturnType<typeof useRows<LicenceKind>>;
}) {
  const [open, setOpen] = useState<LicenceKind | 'new' | null>(null);
  const { rows } = list;
  return (
    <>
      <SettingsSection
        title="Licences you track"
        line={LICENCES_LINE}
        {...(canEdit
          ? {
              action: (
                <Button variant="primary" onPress={() => setOpen('new')}>
                  Add a licence
                </Button>
              ),
            }
          : {})}
        status={statusOf(rows, list.error)}
        emptyText="None yet. Add the ones your work needs, for example a gas fitter’s."
        {...(list.error ? { failedText: list.error } : {})}
        onRetry={list.load}
      >
        {rows?.map((k) => (
          <SettingRow
            key={k.id}
            name={k.name}
            {...(k.active ? {} : { tag: 'No longer tracked' })}
            help={[...(k.issuer ? [k.issuer] : []), `reminded ${k.remindDays} days ahead`].join(' · ')}
            control={
              canEdit ? (
                <Link label={`Edit ${k.name}`} onPress={() => setOpen(k)}>
                  Edit
                </Link>
              ) : null
            }
          />
        ))}
      </SettingsSection>
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {open && (
        <KindSheet
          key={open === 'new' ? 'new' : open.id}
          kind={open === 'new' ? null : open}
          taken={(rows ?? []).map((k) => k.code)}
          onSaved={list.load}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function KindSheet({
  kind,
  taken,
  onSaved,
  onClose,
}: {
  kind: LicenceKind | null;
  taken: readonly string[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [first] = useState(() => kindForm(kind));
  const [f, setF] = useState(first);
  const save = useSaveKey(JSON.stringify(f));
  return (
    <FormSheet
      isOpen
      title={kind ? kind.name : 'Add a licence'}
      submitLabel={kind ? 'Save' : 'Add licence'}
      isDirty={JSON.stringify(f) !== JSON.stringify(first)}
      isSubmitDisabled={!f.name.trim()}
      onClose={onClose}
      onSubmit={async () => {
        const built = kindInput(kind, f, taken);
        if ('error' in built) throw new Error(built.error);
        try {
          await client.call('licenceKinds.save', built.input, { idempotencyKey: save.key });
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        save.renew();
        onSaved();
      }}
    >
      <TextField
        label="Name"
        value={f.name}
        placeholder="Gas fitter (class B)"
        autoFocus
        onChange={(name) => setF({ ...f, name })}
      />
      <TextField
        label="Usually issued by"
        value={f.issuer}
        placeholder="optional"
        onChange={(issuer) => setF({ ...f, issuer })}
      />
      <TextField
        label="Remind this many days before it expires"
        inputMode="numeric"
        width="number"
        value={f.remindDays}
        onChange={(remindDays) => setF({ ...f, remindDays })}
      />
      {kind && (
        <Checkbox isSelected={f.active} onChange={(active) => setF({ ...f, active })}>
          Still tracked (off: no job asks for it, and no reminders)
        </Checkbox>
      )}
    </FormSheet>
  );
}
